<script setup lang="ts">
import { computed, ref, useId } from 'vue';
import InputText from 'primevue/inputtext';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';

interface Props {
  modelValue?: string;
  label: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  optional?: boolean;
  type?: string;
  autocomplete?: string;
  maxlength?: number;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  placeholder: '',
  error: '',
  disabled: false,
  required: false,
  optional: false,
  type: 'text',
  autocomplete: undefined,
  maxlength: undefined,
});

const emit = defineEmits<{ 'update:modelValue': [value: string] }>();

const inputId = useId();
const errorId = computed(() => `${inputId}-error`);
const hasError = computed(() => props.error.length > 0);

// Separate class constants for each input state. Tailwind's source scanner sees
// every class name as a literal in this file (libs/ui/src/**/*.vue is in @source scope).
// CRITICAL: never interpolate fragments — every class must be a complete name.
// With ptOptions.mergeSections=true in production, this pt MERGES with the global pt;
// per-instance pt keys override global pt keys for the same key (root).
const INPUT_CLASSES_BASE =
  'h-10 w-full px-3 py-2 text-base bg-field rounded-md text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-offset-0';
const INPUT_CLASSES_NORMAL = 'border border-border-strong focus:ring-primary';
const INPUT_CLASSES_ERROR = 'border border-danger focus:ring-danger';
const INPUT_CLASSES_DISABLED = 'opacity-50 cursor-not-allowed';

// PT root returns an object to both set classes AND override the hardcoded type="text"
// that PrimeVue InputText bakes into its template. Without this, password fields render
// as plain text (security defect). The v-bind="attrs" spread in InputText comes AFTER
// the hardcoded type="text", so our type in PT root object wins.
const inputPt = computed(() => ({
  root: () => {
    const stateCls = hasError.value
      ? INPUT_CLASSES_ERROR
      : INPUT_CLASSES_NORMAL;
    const disabledCls = props.disabled ? ` ${INPUT_CLASSES_DISABLED}` : '';
    const result: Record<string, string | number | undefined> = {
      class: `${INPUT_CLASSES_BASE} ${stateCls}${disabledCls}`.trim(),
      type: props.type,
    };
    if (props.autocomplete !== undefined) {
      result.autocomplete = props.autocomplete;
    }
    if (props.maxlength !== undefined) {
      result.maxlength = props.maxlength;
    }
    return result;
  },
}));

function onInput(e: Event) {
  const target = e.target as HTMLInputElement;
  emit('update:modelValue', target.value);
}

// PODZ-1695: expose an imperative focus() handle so parents (notably
// AppointmentsView's clearSearch) can return focus to the underlying input
// after a clear without reaching into internal PrimeVue DOM. PrimeVue
// InputText's `$el` is the input in most builds; the querySelector fallback
// handles wrapper-rendering modes.
const inputRef = ref<InstanceType<typeof InputText> | null>(null);
function focus(): void {
  const el = (inputRef.value as { $el?: HTMLElement } | null)?.$el;
  if (el instanceof HTMLInputElement) {
    el.focus();
  } else {
    el?.querySelector('input')?.focus();
  }
}
defineExpose({ focus });
</script>

<template>
  <div class="flex flex-col">
    <label
      :for="inputId"
      class="text-sm font-normal text-foreground mb-1"
      :class="{ 'text-muted': disabled }"
    >
      {{ label
      }}<span v-if="required" class="text-danger ml-0.5" aria-hidden="true"
        >*</span
      ><span v-if="optional" class="text-xs text-muted font-normal ml-1"
        >optional</span
      >
    </label>
    <InputText
      :id="inputId"
      ref="inputRef"
      :model-value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :aria-invalid="hasError ? 'true' : undefined"
      :aria-describedby="hasError ? errorId : undefined"
      :pt="inputPt"
      @input="onInput"
    />
    <span
      v-if="hasError"
      :id="errorId"
      class="text-sm text-danger mt-1 flex items-center"
    >
      <FontAwesomeIcon :icon="faCircleExclamation" class="mr-1 size-4" />
      {{ error }}
    </span>
  </div>
</template>
